import { Box, Typography, Button, List } from '@mui/material';
import React, { useEffect } from 'react';
import { useDeviceDetails } from '../../data/DataProviders/DeviceDetailsProvider';
import { sortObjectArrayNaturally } from '../../lib';
import { isDataLoaded, Status } from '../../types';
import AddIcon from '@mui/icons-material/Add';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useHistory } from 'react-router-dom';
import { DataIndicators } from '../DataIndicators';
import { FlatListItem } from '../FlatListItem/FlatListItem';
import { Device } from '../../api/types';
import { useSignalRSiteData } from '../../data/DataProviders/SignalRSiteDataProvider';
import { getUpdatedChildDeviceStatusList } from '../../lib/signalr-helper';

export const ChildDeviceList: React.FC<{ deviceDetails: Function }> = (props) => {
    const deviceDetails = useDeviceDetails();
    const currentAppRoute = useCurrentAppRoute();
    const history = useHistory();
    const isDeviceDetailEmpty = isDataLoaded(deviceDetails) ? deviceDetails.value.devices.length === 0 : false;
    const parentDeviceIsEditable = isDataLoaded(deviceDetails) ? deviceDetails.value.isEditable : false;

    useEffect(() => {
        if (isDataLoaded(deviceDetails)) props.deviceDetails(deviceDetails.value);
    }, [deviceDetails]); //eslint-disable-line react-hooks/exhaustive-deps

    const navigateToChildDevice = (): void => {
        if (currentAppRoute.type === 'DeviceProfile' && isDataLoaded(deviceDetails))
            pushAppRoute(history, {
                type: 'AddChildDevice',
                deviceId: deviceDetails.value.id,
                locationId: currentAppRoute.locationId,
                orgId: currentAppRoute.orgId,
                siteId: currentAppRoute.siteId,
            });
    };

    const realTimeSiteData = useSignalRSiteData();
    const [childDeviceList, setChildDeviceList] = React.useState<Device[]>([]);

    React.useEffect(() => {
        if (isDataLoaded(deviceDetails) && deviceDetails.value.devices) {
            setChildDeviceList(deviceDetails.value.devices);
        }
    }, [deviceDetails]);

    React.useEffect(() => {
        if (
            realTimeSiteData &&
            realTimeSiteData.devices &&
            isDataLoaded(deviceDetails) &&
            deviceDetails.value.devices
        ) {
            const clone = getUpdatedChildDeviceStatusList(realTimeSiteData.devices, deviceDetails.value.devices);
            setChildDeviceList(clone);
        }
    }, [realTimeSiteData]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            {isDataLoaded(deviceDetails) && deviceDetails.value.devices.length === 0 && (
                <Typography variant="h6" color="primary" style={{ marginBottom: 24 }}>
                    {`Devices (0)`}
                </Typography>
            )}
            <DataIndicators
                errorMessage="No Devices found"
                emptyMessage="No Devices found"
                data={deviceDetails}
                emptyIcon="Device"
                isEmpty={isDeviceDetailEmpty}
                withAction={true}
                description="Devices that cannot connect directly to the Eaton Cloud can be added here. These Devices communicate with a Connected Device, which then connects to the Eaton Cloud."
                action={
                    parentDeviceIsEditable ? (
                        <Button
                            startIcon={<AddIcon fontSize="large" style={{ color: 'white' }} />}
                            color="primary"
                            data-testid="add-child-device-button"
                            onClick={(): void => {
                                navigateToChildDevice();
                            }}
                            variant="contained"
                        >
                            Add Device
                        </Button>
                    ) : (
                        <> </>
                    )
                }
            />
            {isDataLoaded(deviceDetails) &&
                deviceDetails.value.devices.length > 0 &&
                currentAppRoute.type === 'DeviceProfile' && (
                    <>
                        <Box display="flex" justifyContent="space-between" style={{ padding: 16, paddingTop: 0 }}>
                            <Typography variant="h6" color="primary" data-testid="child-delete-count">
                                {`Devices (${deviceDetails.value.devices.length})`}
                            </Typography>
                            {deviceDetails.value.isEditable && (
                                <Button
                                    startIcon={<AddIcon fontSize="large" style={{ color: 'white' }} />}
                                    color="primary"
                                    data-testid="add-child-delete"
                                    onClick={(): void => {
                                        navigateToChildDevice();
                                    }}
                                    variant="contained"
                                >
                                    Add Device
                                </Button>
                            )}
                        </Box>
                        {childDeviceList && (
                            <List data-testid="device-item">
                                {sortObjectArrayNaturally(childDeviceList, [{ sortField: 'name' }]).map(
                                    (device: Device) => (
                                        <FlatListItem
                                            key={device.id}
                                            item={{
                                                id: device.id,
                                                name: device.name || '',
                                                status: device.status ? Status.Online : Status.Offline,
                                                isEditable: device.isEditable || false,
                                                parentId: device.parentDeviceId || '',
                                            }}
                                            subTitleData={`${device.vendor} ${device.model}|${device.family}`}
                                            hasStatus={true}
                                            navigate={{
                                                type: 'ChildDeviceProfile',
                                                deviceId: deviceDetails.value.id,
                                                locationId: currentAppRoute.locationId,
                                                orgId: currentAppRoute.orgId,
                                                siteId: currentAppRoute.siteId,
                                                childDeviceId: device.id,
                                            }}
                                        ></FlatListItem>
                                    )
                                )}
                            </List>
                        )}
                    </>
                )}
        </>
    );
};
